import * as THREE from 'three';
import { canvas2d, roundedRectPath, seededRandom, tileTexture } from './helpers';

/**
 * COL-011B floor art — the eight new paintable floor looks (three path styles + five floral
 * styles). Every canvas is one 0.48 paving subcell, drawn to wrap so seams line up with the
 * grout band. The three legacy tiles (classic / brick / stone) live in helpers.ts.
 *
 * Also hosts the two soft radial textures the night pass uses for bulb halos and lamp pools.
 */

const TILE = 128;

function fillTile(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, TILE, TILE);
}

/** Bright kids tile: 2x2 pastel checker (mint / pink / sky / butter) with a white grout and a glossy corner. */
export function makeKidsTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(TILE, TILE);
  fillTile(ctx, '#fbf7ee');
  const tones = ['#8fe0c2', '#ffb3c7', '#9ccfff', '#ffe08a'];
  const half = TILE / 2;
  const gap = 6;
  for (let row = 0; row < 2; row += 1) {
    for (let col = 0; col < 2; col += 1) {
      const x = col * half + gap / 2;
      const y = row * half + gap / 2;
      ctx.fillStyle = tones[row * 2 + col]!;
      roundedRectPath(ctx, x, y, half - gap, half - gap, 12);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      roundedRectPath(ctx, x + 7, y + 6, half - gap - 14, 14, 7);
      ctx.fill();
    }
  }
  return tileTexture(canvas);
}

/** Wood boardwalk: three honey planks with grain lines, dark gaps and a couple of nail heads. */
export function makeWoodTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(TILE, TILE);
  fillTile(ctx, '#7a4d2a');
  const random = seededRandom(41);
  const tones = ['#d99a58', '#e0a462', '#d2924f', '#e6ad6d'];
  const plankH = TILE / 3;
  const gap = 5;
  for (let i = 0; i < 3; i += 1) {
    const y = i * plankH + gap / 2;
    ctx.fillStyle = tones[i % tones.length]!;
    roundedRectPath(ctx, 0, y, TILE, plankH - gap, 4);
    ctx.fill();
    ctx.strokeStyle = 'rgba(120, 70, 30, 0.28)';
    ctx.lineWidth = 2;
    for (let g = 0; g < 3; g += 1) {
      const gy = y + 8 + g * 11 + random() * 4;
      ctx.beginPath();
      ctx.moveTo(0, gy);
      ctx.bezierCurveTo(40, gy - 3 + random() * 6, 90, gy + 3 - random() * 6, TILE, gy);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(255, 230, 190, 0.35)';
    roundedRectPath(ctx, 6, y + 3, TILE - 12, 6, 3);
    ctx.fill();
    ctx.fillStyle = '#5a3a1e';
    ctx.beginPath();
    ctx.arc(14, y + plankH / 2 - 2, 2.5, 0, Math.PI * 2);
    ctx.arc(TILE - 14, y + plankH / 2 - 2, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
  return tileTexture(canvas);
}

function starPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, outer: number, inner: number, points = 5): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / points;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
}

/** Festive: deep plum tile with a gold frame, a centre star and confetti dots — the night-friendly premium path. */
export function makeFestiveTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(TILE, TILE);
  fillTile(ctx, '#c99a3c');
  ctx.fillStyle = '#4b2a6e';
  roundedRectPath(ctx, 5, 5, TILE - 10, TILE - 10, 12);
  ctx.fill();
  ctx.fillStyle = '#5b367f';
  roundedRectPath(ctx, 12, 12, TILE - 24, TILE - 24, 10);
  ctx.fill();
  ctx.strokeStyle = '#f2c860';
  ctx.lineWidth = 3;
  roundedRectPath(ctx, 17, 17, TILE - 34, TILE - 34, 8);
  ctx.stroke();
  ctx.fillStyle = '#ffd97a';
  starPath(ctx, TILE / 2, TILE / 2, 22, 10);
  ctx.fill();
  ctx.fillStyle = '#fff1b8';
  starPath(ctx, TILE / 2, TILE / 2 - 2, 10, 4.5);
  ctx.fill();
  const dots = ['#ff8fb1', '#8fe0ff', '#ffe08a', '#b6ff9c'];
  const spots = [
    [30, 30],
    [98, 30],
    [30, 98],
    [98, 98],
    [64, 26],
    [64, 102],
    [26, 64],
    [102, 64],
  ] as const;
  spots.forEach(([x, y], i) => {
    ctx.fillStyle = dots[i % dots.length]!;
    ctx.beginPath();
    ctx.arc(x, y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  });
  return tileTexture(canvas);
}

interface FloralField {
  /** Lawn base colours (cycled). */
  ground: string[];
  /** Petal colours (cycled). */
  petals: string[];
  center: string;
  /** Number of blooms per tile. */
  count: number;
  seed: number;
  petalRadius: number;
  /** Optional darker leaves tucked around blooms. */
  leaves?: string;
}

function drawBloom(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, petal: string, center: string): void {
  ctx.fillStyle = petal;
  for (let p = 0; p < 5; p += 1) {
    const a = (p / 5) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * r * 0.62, y + Math.sin(a) * r * 0.62, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = center;
  ctx.beginPath();
  ctx.arc(x, y, r * 0.32, 0, Math.PI * 2);
  ctx.fill();
}

function drawLeaf(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, angle: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.45, angle, 0, Math.PI * 2);
  ctx.fill();
}

function makeFloralTexture(field: FloralField): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(TILE, TILE);
  fillTile(ctx, field.ground[0]!);
  const random = seededRandom(field.seed);
  // mottled lawn
  for (let i = 0; i < 26; i += 1) {
    ctx.fillStyle = field.ground[i % field.ground.length]!;
    ctx.globalAlpha = 0.5;
    const x = random() * TILE;
    const y = random() * TILE;
    const r = 10 + random() * 16;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.7, random() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  // blooms kept inside the tile so seams stay clean
  const margin = field.petalRadius + 4;
  for (let i = 0; i < field.count; i += 1) {
    const x = margin + random() * (TILE - margin * 2);
    const y = margin + random() * (TILE - margin * 2);
    const r = field.petalRadius * (0.8 + random() * 0.4);
    if (field.leaves) {
      drawLeaf(ctx, x + r * 0.9, y + r * 0.6, r * 0.8, random() * Math.PI, field.leaves);
      drawLeaf(ctx, x - r * 0.9, y - r * 0.5, r * 0.8, random() * Math.PI, field.leaves);
    }
    drawBloom(ctx, x, y, r, field.petals[i % field.petals.length]!, field.center);
  }
  return tileTexture(canvas);
}

/** Pink flower bed: rose and blush blooms on a fresh lawn. */
export function makeFlowerPinkTileTexture(): THREE.CanvasTexture {
  return makeFloralTexture({
    ground: ['#86c95a', '#93d366', '#7fc154'],
    petals: ['#ff8fb8', '#ff6fa4', '#ffb3cf', '#f75f97'],
    center: '#fff0a8',
    count: 7,
    seed: 101,
    petalRadius: 11,
    leaves: '#4f9a3a',
  });
}

/** Yellow flower bed: sunny daisies and marigolds with orange hearts. */
export function makeFlowerYellowTileTexture(): THREE.CanvasTexture {
  return makeFloralTexture({
    ground: ['#8bcb5c', '#98d56a', '#84c455'],
    petals: ['#ffe066', '#ffd23f', '#fff0a0', '#ffb833'],
    center: '#ff8a3c',
    count: 7,
    seed: 202,
    petalRadius: 11,
    leaves: '#4f9a3a',
  });
}

/** Mixed flowers: a confetti meadow of pink, sky, lilac, yellow and white blooms. */
export function makeFlowerMixTileTexture(): THREE.CanvasTexture {
  return makeFloralTexture({
    ground: ['#88cc5e', '#96d46a', '#7ec456'],
    petals: ['#ff86b6', '#8fd0ff', '#c79bff', '#ffe066', '#ffffff', '#ff9a5c'],
    center: '#fff4c2',
    count: 9,
    seed: 303,
    petalRadius: 10,
    leaves: '#4f9a3a',
  });
}

/** Soft clover ground: dense three-leaf clovers on a deeper green with a few tiny white flowers. */
export function makeCloverTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(TILE, TILE);
  fillTile(ctx, '#6bb24e');
  const random = seededRandom(404);
  const tones = ['#5faa45', '#78c05a', '#66b34c'];
  for (let i = 0; i < 22; i += 1) {
    ctx.fillStyle = tones[i % tones.length]!;
    ctx.globalAlpha = 0.6;
    const x = random() * TILE;
    const y = random() * TILE;
    const r = 10 + random() * 14;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.7, random() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  const leafTones = ['#3f8f32', '#47a03a', '#358a2c'];
  for (let i = 0; i < 12; i += 1) {
    const x = 12 + random() * (TILE - 24);
    const y = 12 + random() * (TILE - 24);
    const r = 5 + random() * 3;
    const spin = random() * Math.PI * 2;
    ctx.fillStyle = leafTones[i % leafTones.length]!;
    for (let p = 0; p < 3; p += 1) {
      const a = spin + (p / 3) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(x + Math.cos(a) * r * 0.75, y + Math.sin(a) * r * 0.75, r * 0.62, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.arc(x + Math.cos(spin) * r * 0.75 - 1, y + Math.sin(spin) * r * 0.75 - 1, r * 0.25, 0, Math.PI * 2);
    ctx.fill();
  }
  for (let i = 0; i < 4; i += 1) {
    const x = 14 + random() * (TILE - 28);
    const y = 14 + random() * (TILE - 28);
    drawBloom(ctx, x, y, 5.5, '#ffffff', '#ffe58a');
  }
  return tileTexture(canvas);
}

/** Premium ornamental garden carpet: clipped hedge border, cream gravel field and a red-and-gold medallion. */
export function makeGardenTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(TILE, TILE);
  fillTile(ctx, '#3f8a3a');
  ctx.fillStyle = '#4c9c44';
  roundedRectPath(ctx, 3, 3, TILE - 6, TILE - 6, 10);
  ctx.fill();
  ctx.fillStyle = '#f3e6c6';
  roundedRectPath(ctx, 18, 18, TILE - 36, TILE - 36, 10);
  ctx.fill();
  ctx.fillStyle = 'rgba(200, 180, 140, 0.35)';
  roundedRectPath(ctx, 24, 24, TILE - 48, TILE - 48, 8);
  ctx.fill();
  // hedge texture bumps along the border
  const random = seededRandom(505);
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  for (let i = 0; i < 40; i += 1) {
    const edge = Math.floor(random() * 4);
    const t = random() * TILE;
    const [x, y] = edge === 0 ? [t, 9] : edge === 1 ? [t, TILE - 9] : edge === 2 ? [9, t] : [TILE - 9, t];
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  // medallion
  ctx.fillStyle = '#d8a83c';
  ctx.beginPath();
  ctx.arc(TILE / 2, TILE / 2, 24, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#e04a5a';
  drawBloom(ctx, TILE / 2, TILE / 2, 15, '#e04a5a', '#ffd97a');
  ctx.fillStyle = '#d8a83c';
  for (let i = 0; i < 4; i += 1) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    ctx.beginPath();
    ctx.arc(TILE / 2 + Math.cos(a) * 36, TILE / 2 + Math.sin(a) * 36, 4, 0, Math.PI * 2);
    ctx.fill();
  }
  return tileTexture(canvas);
}

/** Soft radial halo used behind every bulb / lamp at night (additive). */
export function makeGlowTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(64, 64);
  const gradient = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(0.6, 'rgba(255,255,255,0.14)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Wide, faint radial pool laid flat on the ground under lamps and lit attractions. */
export function makeLightPoolTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(128, 128);
  const gradient = ctx.createRadialGradient(64, 64, 6, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(255,255,255,0.7)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.3)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 128, 128);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
